import _ from "lodash";
import moment from "moment";
import React from "react";
import { getAcademicYearLabel } from "Platform/components/GlobalHeader/components/PlatformSwitcher/PlatformSwitcherUtils";
import { getCurrentPathname } from "services/NavigationService";
import aclStorage from "lib/aclService";
import ACLStore from "lib/aclStore";
import { store } from "store/createStore";
import {
  getUserSortOptions,
  getFormattedUserName,
} from "Utils/GetFormattedUserName";
import { USER_NAME_SORT_OPTIONS_V2 } from "SchoolSetup/modules/SchoolSetupModule";
import {
  getOrganizationId,
  getOrganizationCurriculumPrograms,
} from "modules/CommonSelectors";
import { getBehaviourNomenclatureFromCache } from "BehaviourConfiguration/modules/BehaviourConfigurationGraphqlHelpers";
import {
  getIsAccountConfigurationEnabled,
  isStudentBlockingFeatureEnabled,
  getIsAdminNavigationFor10Accounts,
} from "Utils/ACLStoreUtils";
import { getAllCurriculumsAdminConfigurationsObject } from "ChildrenPage/modules/ChildrenPageUtils";
import {
  getRnPLevelsFeedQuery,
  getRnPPermissionsFeedQuery,
} from "RolesAndPermissions/modules/RolesAndPermissionsQuery";
import { getSettingValue } from "modules/PermissionModule";
import {
  RNP_ACCOUNT_LEVEL_LABEL_PREFERENCE,
  RNP_PROGRAMME_LEVEL_LABEL_PREFERENCE,
  RNP_LEARNING_COURSE_LEVEL_LABEL_PREFERENCE,
  RNP_CLASS_LEVEL_LABEL_PREFERENCE,
  ENABLE_BEHAVIOUR_MODULE_PERM,
  ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR,
  SNPv2_PERM,
  ENABLE_MODULE_SETTINGS_IN_PROGRAMME_WIDE_CONFIGURATIONS,
  ENABLE_BIG_ORG_CHANGES_IN_RNP,
} from "Constants/permissionConstants";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getOrganizationGlobalConstantsQueryVariablesMemoized } from "modules/Services";
import { getOrganizationGlobalConstantsFromCache } from "modules/CommonGraphqlHelpers";
import { isNewEventCreationFlowEnabled } from "MyCalendar/modules/Utils";
import { getIsLearningCoursesEnabledForOrganization } from "LearningCourses/modules/Utils";

import {
  CopyOutlined,
  DeleteOutlined,
  PencilLineOutlined,
  OpenBookOutlined,
  SchoolOutlined,
  AnalyticsOutlined,
  LoudspeakerOutlined,
  AssessmentOutlined,
  AttendanceOutlined,
  AttendanceGearOutlined,
  PolicyTickOutlined,
  HeadHeartOutlined,
  HeadGearOutlined,
  CalendarOutlined,
  FolderOutlined,
  DashboardOutlined,
  ClassSettingsOutlined,
  PostThreeOutlined,
  TickFrameOutlined,
  UserArrowInwardOutlined,
  FamilyOutlined,
  SettingsOutlined,
  TableOutlined,
  ScoreBasedAssessmentOutlined,
  AssessmentandEvaluationOutlined,
  WorksheetClipboardOutlined,
  ClipboardPencilLinesOutlined,
  AddToLibraryOutlined,
  CommentSquareLeftClassroomOutlined,
  InsightsSquareOutlined,
  JournalOutlined,
  SetupOutlined,
  GraduationCapOutlined,
  ProgressReportOutlined,
  OpenBookTextOutlined,
  ReportGearOutlined,
  BooksTwoOutlined,
  PolicyOutlined,
  StaffMemberOutlined,
  MultipleUsersOutlined,
  ProfileOutlined,
  GoalsOutlined,
  StudentEditOutlined,
  StudentOutlined,
  SpiralBookLinesOutlined,
  CalendarScheduleWeekOutlined,
  ClassProgressOutlined,
  UnitPlanOutlined,
  CalendarScheduleYearOutlined,
  BookmarkOutlined,
  TickCrossOutlined,
  NotificationBellOutlined,
  AssessmentApplicableOutlined,
  HomeOutlined,
  ListDashedSlantedOutlined,
  ClockRecentsOutlined,
  ListNumberedOutlined,
  ClockArrowsOutlined,
  CalendarScheduleOutlined,
  FlagOutlined,
  QuickTaskOutlined,
  DotsHorizontalOutlined,
  ChevronRightOutlined,
} from "@toddle-edu/ds-icons";
import {
  Dropdown,
  DropdownMenu,
  IconButton,
  Tooltip,
} from "@toddle-edu/ds-web";
import classNames from "classnames";
import { actionDropdownOverlayStyles } from "../styles";
import tableClasses from "../components/RolesAndPermissions.scss";

const ICON_COMPONENTS = {
  OpenBookOutlined,
  SchoolOutlined,
  AnalyticsOutlined,
  LoudspeakerOutlined,
  AssessmentOutlined,
  AttendanceOutlined,
  AttendanceGearOutlined,
  PolicyTickOutlined,
  HeadHeartOutlined,
  HeadGearOutlined,
  DeleteOutlined,
  CalendarOutlined,
  FolderOutlined,
  DashboardOutlined,
  ClassSettingsOutlined,
  PostThreeOutlined,
  TickFrameOutlined,
  UserArrowInwardOutlined,
  FamilyOutlined,
  SettingsOutlined,
  TableOutlined,
  ScoreBasedAssessmentOutlined,
  AssessmentandEvaluationOutlined,
  WorksheetClipboardOutlined,
  ClipboardPencilLinesOutlined,
  AddToLibraryOutlined,
  CommentSquareLeftClassroomOutlined,
  InsightsSquareOutlined,
  JournalOutlined,
  SetupOutlined,
  GraduationCapOutlined,
  ProgressReportOutlined,
  OpenBookTextOutlined,
  ReportGearOutlined,
  BooksTwoOutlined,
  PolicyOutlined,
  StaffMemberOutlined,
  MultipleUsersOutlined,
  ProfileOutlined,
  GoalsOutlined,
  StudentEditOutlined,
  StudentOutlined,
  SpiralBookLinesOutlined,
  CalendarScheduleWeekOutlined,
  ClassProgressOutlined,
  UnitPlanOutlined,
  CalendarScheduleYearOutlined,
  BookmarkOutlined,
  TickCrossOutlined,
  NotificationBellOutlined,
  AssessmentApplicableOutlined,
  HomeOutlined,
  ListDashedSlantedOutlined,
  ClockRecentsOutlined,
  ListNumberedOutlined,
  ClockArrowsOutlined,
  CalendarScheduleOutlined,
  FlagOutlined,
  QuickTaskOutlined,
};

const ROLES_AND_PERMISSIONS_TABS = {
  ACCOUNT_ROLES: "ACCOUNT_ROLES",
  PROGRAMME_ROLES: "PROGRAMME_ROLES",
  LEARNING_COURSE_ROLES: "LEARNING_COURSE_ROLES",
  CLASS_ROLES: "CLASS_ROLES",
};

const ROLES_AND_PERMISSIONS_LEVELS = {
  ACCOUNT_LEVEL: "ACCOUNT_LEVEL",
  PROGRAMME_LEVEL: "PROGRAMME_LEVEL",
  LEARNING_COURSE_LEVEL: "LEARNING_COURSE_LEVEL",
  CLASS_LEVEL: "CLASS_LEVEL",
};

const LEVELS_ROLES_MAP = {
  [ROLES_AND_PERMISSIONS_LEVELS.ACCOUNT_LEVEL]:
    ROLES_AND_PERMISSIONS_TABS.ACCOUNT_ROLES,
  [ROLES_AND_PERMISSIONS_LEVELS.PROGRAMME_LEVEL]:
    ROLES_AND_PERMISSIONS_TABS.PROGRAMME_ROLES,
  [ROLES_AND_PERMISSIONS_LEVELS.LEARNING_COURSE_LEVEL]:
    ROLES_AND_PERMISSIONS_TABS.LEARNING_COURSE_ROLES,
  [ROLES_AND_PERMISSIONS_LEVELS.CLASS_LEVEL]:
    ROLES_AND_PERMISSIONS_TABS.CLASS_ROLES,
};

const DEFAULT_ROLE_UID_AND_LEVEL_MAP = {
  [ROLES_AND_PERMISSIONS_LEVELS.ACCOUNT_LEVEL]: "NON_ADMIN_USER",
  [ROLES_AND_PERMISSIONS_LEVELS.PROGRAMME_LEVEL]: "PROGRAMME_TEACHER",
  [ROLES_AND_PERMISSIONS_LEVELS.LEARNING_COURSE_LEVEL]: "COURSE_VIEWER",
  [ROLES_AND_PERMISSIONS_LEVELS.CLASS_LEVEL]: "CLASS_TEACHER",
};

const canManageRolesAndPermissions = level => {
  const {
    ACCOUNT_LEVEL,
    PROGRAMME_LEVEL,
    LEARNING_COURSE_LEVEL,
    CLASS_LEVEL,
  } = ROLES_AND_PERMISSIONS_LEVELS;

  switch (level) {
    case ACCOUNT_LEVEL:
      return aclStorage.getPermissionValue(
        "RolesAndPermissions:CanManageAccountLevelRoles"
      );
    case PROGRAMME_LEVEL:
    case CLASS_LEVEL:
    case LEARNING_COURSE_LEVEL:
      return aclStorage.getPermissionValue(
        "RolesAndPermissions:CanManageNonAccountLevelRoles"
      );
    default:
      return false;
  }
};

const isCustomRoleCreationAllowed = () => {
  return ACLStore.can("FeatureFlag:AllowCustomRoleCreationInRnP");
};

const getIsBigOrgRnPChangesEnabled = () => {
  return ACLStore.can(ENABLE_BIG_ORG_CHANGES_IN_RNP);
};

const getActionMenuTabs = ({ role, t }) => {
  const { isSuperAdmin = false, isCustomRole = false } = role;

  const tabs = [
    {
      key: "editDescription",
      label: t("common:edit_with_label", {
        label: t("common:description"),
      }),
      icon: <PencilLineOutlined />,
      disabled: isSuperAdmin,
      disabledTooltip: t("acl:edit_description_disabled_action_tooltip"),
      tooltipPlacement: "left",
      visible: !isCustomRole,
    },
    {
      key: "editNameAndDescription",
      label: t("common:edit_with_label", {
        label: t("common:name_and_description"),
      }),
      icon: <PencilLineOutlined />,
      visible: isCustomRole,
    },
    {
      key: "duplicate",
      label: t("common:duplicate"),
      icon: <CopyOutlined />,
      visible: isCustomRoleCreationAllowed(),
    },
    {
      key: "delete",
      label: t("common:delete"),
      icon: <DeleteOutlined />,
      isDestructive: true,
      visible: true,
      disabled: !isCustomRole,
      disabledTooltip: t("acl:delete_role_disabled_action_tooltip"),
      tooltipPlacement: "left",
    },
  ];

  return _.filter(tabs, "visible");
};

const onActionItemClick = ({ key, toggleModalType }) => {
  switch (key) {
    case "editNameAndDescription":
    case "editDescription":
      toggleModalType("EDIT_ROLE");
      break;
    case "duplicate":
      toggleModalType("DUPLICATE_ROLE");
      break;
    case "delete":
      toggleModalType("DELETE_ROLE");
      break;
    default:
      break;
  }
};

const getColumnList = (isManageRolesAndPermissionsEnabled, aySubtext) => {
  const rolesDetailsColumns = [
    {
      label: "Role Name",
      locale: "common:role_name",
      id: "name",
      type: "TEXTCOLUMN",
      style: {
        maxWidth: "252px",
        padding: "12px",
        verticalAlign: "baseline",
      },
      visible: true,
    },
    {
      label: "Definition",
      locale: "common:definition",
      id: "definition",
      type: "CUSTOM",
      style: {
        padding: "12px",
        verticalAlign: "baseline",
      },
      visible: true,
    },
    {
      label: "Staff members",
      locale: "common:staff_members_mapped_to_role",
      id: "staffMembers",
      type: "CUSTOM",
      subText: aySubtext,
      style: {
        maxWidth: "254px",
        padding: "12px",
        verticalAlign: "baseline",
      },
      visible: true,
    },
    {
      label: "Actions",
      locale: "common:action_plural",
      id: "action",
      type: "CUSTOM",
      style: {
        maxWidth: "88px",
        verticalAlign: "top",
        paddingTop: "12px",
        paddingBottom: "12px",
      },
      visible: isManageRolesAndPermissionsEnabled,
    },
    {
      label: "",
      id: "actionRightArrow",
      type: "CUSTOM",
      style: {
        maxWidth: "64px",
        verticalAlign: "top",
        paddingTop: "12px",
        paddingBottom: "12px",
      },
      visible: !isManageRolesAndPermissionsEnabled,
    },
  ];
  return rolesDetailsColumns.filter(column => column.visible);
};

const getActionMenuTabsV2 = ({ role, t }) => {
  const { isSuperAdmin = false, isCustomRole = false } = role;

  const tabs = [
    {
      key: "editDescription",
      label: t("common:edit"),
      icon: <PencilLineOutlined />,
      disabled: isSuperAdmin,
      disabledTooltip: t("acl:edit_description_disabled_action_tooltip"),
      tooltipPlacement: "left",
      visible: true,
    },
    {
      key: "viewPermissions",
      label: t("common:view_with_label_lowercase", {
        label: t("common:permission_plural"),
      }),
      icon: <ListDashedSlantedOutlined />,
      visible: true,
    },
    {
      key: "duplicate",
      label: t("common:duplicate"),
      icon: <CopyOutlined />,
      visible: isCustomRoleCreationAllowed(),
    },
    {
      key: "delete",
      label: t("common:delete"),
      icon: <DeleteOutlined />,
      isDestructive: true,
      visible: true,
      disabled: !isCustomRole,
      disabledTooltip: t("acl:delete_role_disabled_action_tooltip"),
      tooltipPlacement: "left",
    },
  ];

  return _.filter(tabs, "visible");
};

const getRnPFeedTableHeaders = ({ t, aySubtext }) => {
  return [
    {
      key: "roleId",
      value: t("common:id"),
      style: { borderTopLeftRadius: "8px" },
    },
    {
      key: "role",
      value: t("common:role"),
      style: { minWidth: "200px", maxWidth: "200px" },
    },
    {
      key: "description",
      value: t("common:definition"),
      style: { minWidth: "400px", maxWidth: "400px" },
    },
    {
      key: "staffMembers",
      value: t("common:staff_members_mapped_to_role"),
      subText: aySubtext,
    },
    {
      key: "createdBy",
      value: t("common:created_by"),
    },
    {
      key: "lastUpdatedBy",
      value: t("common:last_updated_by"),
    },
    {
      key: "action",
      value: "",
      style: { maxWidth: "80px", borderTopRightRadius: "8px" },
    },
  ];
};

const getDateInSchoolTimezone = date => {
  const orgId = getOrganizationId();
  const queryVariables = getOrganizationGlobalConstantsQueryVariablesMemoized(
    orgId
  );
  const constantsData = getOrganizationGlobalConstantsFromCache(queryVariables);
  const globalConstants = _.get(constantsData, "node.globalConstants");
  const timezoneObj = _.find(
    globalConstants,
    ({ name }) => name === "TIMEZONE"
  );
  const timeZone = _.get(timezoneObj, "value");
  return timeZone ? moment(date).tz(timeZone) : moment(date);
};

const formatDate = date => {
  if (_.isNil(date)) return null;
  return getDateInSchoolTimezone(date).format("D MMM YYYY, h:mma");
};

const renderCreatedOrUpdatedByCell = ({ user, date }) => {
  const formattedDate = formatDate(date);
  if (!user || !formattedDate) return <div>-</div>;

  const fullName = getFormattedUserName({ user, type: "staff" });

  return (
    <div className={tableClasses.createdOrUpdatedByCell}>
      <div className="text-body">{fullName}</div>
      <div className="text-secondary text-body">{formattedDate}</div>
    </div>
  );
};

const generateRnPFeedTableData = ({
  rnpView,
  t,
  headers,
  isManageRolesAndPermissionsEnabled,
  toggleModalType,
  updateState,
  componentTestId,
  goToManagePermissionPage,
  setMultiStepModalMode,
}) => {
  const totalColumns = _.size(headers);
  const totalRows = _.size(rnpView);

  return _.map(rnpView, (role, rowIndex) => {
    const isLastRow = rowIndex === totalRows - 1;
    const {
      id,
      displayUid,
      label,
      description,
      assignedUserCount,
      assignedUserCountWithArchived,
      createdAt,
      createdBy,
      updatedAt,
      updatedBy,
    } = role;

    const tabs = getActionMenuTabsV2({ role, t });

    const onActionMenuItemClick = ({ key }) => {
      updateState({ roleDetails: role });
      if (key === "viewPermissions") {
        goToManagePermissionPage(id);
        return;
      }
      if (key === "editDescription") {
        setMultiStepModalMode("edit");
        return;
      }
      onActionItemClick({ key, toggleModalType });
    };

    const rowData = _.map(headers, ({ key }, columnIndex) => {
      let value;
      const isFirstColumn = columnIndex === 0;
      const isLastColumn = columnIndex === totalColumns - 1;

      const shouldApplyBorderRadius =
        isLastRow && (isFirstColumn || isLastColumn);

      const cellStyle = shouldApplyBorderRadius
        ? {
            ...(isFirstColumn && {
              borderRadius: "0px 0px 0px 8px",
            }),
            ...(isLastColumn && {
              borderRadius: "0px 0px 8px 0px",
            }),
          }
        : { borderRadius: "0px" };

      switch (key) {
        case "role":
          value = (
            <Tooltip
              tooltip={<span>{label}</span>}
              placement="bottom"
              dsVersion="2.0"
              showArrow={true}
            >
              <span className={tableClasses.roleCell}>{label}</span>
            </Tooltip>
          );
          break;

        case "description":
          value = (
            <div className={tableClasses.descriptionCell}>{description}</div>
          );
          break;

        case "staffMembers": {
          const isDisabled = assignedUserCountWithArchived === 0;
          value = (
            <button
              onClick={e => {
                e.stopPropagation();
                toggleModalType("USERS_MAPPED");
                updateState({ roleDetails: role });
              }}
              className={classNames("text-label", {
                [tableClasses.staffMemberButton]: !isDisabled,
                [tableClasses.staffMemberButtonDisabled]: isDisabled,
              })}
              disabled={isDisabled}
              data-test-id={`${componentTestId}-staffMembers-button`}
            >
              {t("common:staff_members_count_with_status", {
                label: getStaffMemberStatusPrefixText({
                  assignedUsersTotalCount: assignedUserCountWithArchived,
                  t,
                }),
                count: assignedUserCount,
              })}
            </button>
          );
          break;
        }

        case "roleId":
          value = displayUid;
          break;

        case "createdBy":
          value = renderCreatedOrUpdatedByCell({
            user: createdBy,
            date: createdAt,
          });
          break;

        case "lastUpdatedBy":
          value = renderCreatedOrUpdatedByCell({
            user: updatedBy,
            date: updatedAt,
          });
          break;

        case "action":
          value = isManageRolesAndPermissionsEnabled ? (
            <div className={tableClasses.actionContainer}>
              <Dropdown
                overlayStyle={actionDropdownOverlayStyles}
                overlay={
                  <DropdownMenu
                    testId={`${componentTestId}-roleActions-dropdown`}
                    onClick={onActionMenuItemClick}
                    options={tabs}
                    shouldStopPropagation={true}
                    dsVersion="2.0"
                  />
                }
                placement="bottomRight"
              >
                <IconButton
                  testId={`${componentTestId}-roleActions`}
                  icon={<DotsHorizontalOutlined />}
                  variant="neutral"
                  type="plain"
                  size="small"
                  dsVersion="2.0"
                  shouldStopPropagation={true}
                  aria-label={t("common:actions")}
                />
              </Dropdown>
              <IconButton
                icon={<ChevronRightOutlined />}
                variant="neutral"
                type="plain"
                size="small"
                dsVersion="2.0"
                aria-label={t("common:view_with_label_lowercase", {
                  label: t("common:permission_plural"),
                })}
              />
            </div>
          ) : (
            <IconButton
              icon={<ChevronRightOutlined />}
              variant="neutral"
              type="plain"
              size="small"
              dsVersion="2.0"
              aria-label={t("common:view_with_label_lowercase", {
                label: t("common:permission_plural"),
              })}
            />
          );
          break;

        default:
          value = null;
      }

      return { key, value, style: cellStyle };
    });

    return { id, rowData };
  });
};

const getUsersMappedModalColumns = ({ level, t }) => {
  const {
    ACCOUNT_LEVEL,
    PROGRAMME_LEVEL,
    LEARNING_COURSE_LEVEL,
    CLASS_LEVEL,
  } = ROLES_AND_PERMISSIONS_LEVELS;
  const accountLevelColumns = [
    {
      label: "Name",
      locale: "common:name",
      id: "name",
      type: "NAMEWITHIMAGE",
      isSticky: true,
      sortType: "dropdown",
      hasSortingEnabled: true,
      sortByOptions: getUserSortOptions({
        sortOptions: USER_NAME_SORT_OPTIONS_V2,
        keyToMap: "sortEntity",
        type: "staff",
      }),
      entity: "NAME",
      showArchivedLabel: false,
    },
  ];

  const programmeLevelColumns = [
    {
      label: "Name",
      locale: "common:name",
      id: "name",
      type: "NAMEWITHIMAGE",
      style: { width: "50%" },
      isSticky: true,
      sortType: "dropdown",
      hasSortingEnabled: true,
      sortByOptions: getUserSortOptions({
        sortOptions: USER_NAME_SORT_OPTIONS_V2,
        keyToMap: "sortEntity",
        type: "staff",
      }),
      entity: "NAME",
      showArchivedLabel: false,
    },
    {
      locale: getIsAccountConfigurationEnabled()
        ? "common:school_plural"
        : "common:programmes",
      id: "programme",
      type: "CUSTOM",
      visible: true,
    },
  ];

  const classLevelColumns = [
    {
      label: "Name",
      locale: "common:name",
      id: "name",
      type: "NAMEWITHIMAGE",
      style: { width: "50%" },
      isSticky: true,
      sortType: "dropdown",
      hasSortingEnabled: true,
      sortByOptions: getUserSortOptions({
        sortOptions: USER_NAME_SORT_OPTIONS_V2,
        keyToMap: "sortEntity",
        type: "staff",
      }),
      entity: "NAME",
      showArchivedLabel: false,
    },
    {
      label: t("common:class_plural"),
      id: "courses",
      type: "CUSTOM",
    },
  ];

  const learningCourseLevelColumns = [
    {
      label: "Name",
      locale: "common:name",
      id: "name",
      type: "NAMEWITHIMAGE",
      style: { width: "50%" },
      isSticky: true,
      sortType: "dropdown",
      hasSortingEnabled: true,
      sortByOptions: getUserSortOptions({
        sortOptions: USER_NAME_SORT_OPTIONS_V2,
        keyToMap: "sortEntity",
        type: "staff",
      }),
      entity: "NAME",
      showArchivedLabel: false,
    },
    {
      label: t("course:courses"),
      id: "learningCourses",
      type: "CUSTOM",
    },
  ];

  switch (level) {
    case ACCOUNT_LEVEL:
      return accountLevelColumns;
    case PROGRAMME_LEVEL:
      return programmeLevelColumns;
    case LEARNING_COURSE_LEVEL:
      return learningCourseLevelColumns;
    case CLASS_LEVEL:
      return classLevelColumns;
    default:
      return accountLevelColumns;
  }
};

const getLevelFilterEnumFromRnPLevel = level => {
  const {
    ACCOUNT_LEVEL,
    PROGRAMME_LEVEL,
    LEARNING_COURSE_LEVEL,
    CLASS_LEVEL,
  } = ROLES_AND_PERMISSIONS_LEVELS;

  switch (level) {
    case ACCOUNT_LEVEL:
      return "ACCOUNT";
    case PROGRAMME_LEVEL:
      return "PROGRAMME";
    case LEARNING_COURSE_LEVEL:
      return "LEARNING_COURSE";
    case CLASS_LEVEL:
      return "COURSE";
    default:
      return "ACCOUNT";
  }
};

const getLevelFromNodeId = nodePath => {
  const {
    ACCOUNT_LEVEL,
    PROGRAMME_LEVEL,
    LEARNING_COURSE_LEVEL,
    CLASS_LEVEL,
  } = ROLES_AND_PERMISSIONS_LEVELS;

  switch (nodePath) {
    case "account-roles":
      return ACCOUNT_LEVEL;
    case "programme-roles":
      return PROGRAMME_LEVEL;
    case "learning-course-roles":
      return LEARNING_COURSE_LEVEL;
    case "class-roles":
      return CLASS_LEVEL;
    default:
      return null;
  }
};

const getRnPLevelNameFromGlobalLevel = ({ globalLevel, t }) => {
  const locale = getRnPLevelLabelLocale(globalLevel);

  return t(`acl:level_with_label`, {
    label: t(`acl:${locale}`, {
      defaultValue: locale,
    }),
  });
};

export const getRnPLevelLabelLocale = globalLevel => {
  let preferenceName;

  const userInfo = getUserInfo();

  const organizationId = userInfo.org_id;

  const {
    ACCOUNT_LEVEL,
    PROGRAMME_LEVEL,
    LEARNING_COURSE_LEVEL,
    CLASS_LEVEL,
  } = ROLES_AND_PERMISSIONS_LEVELS;

  switch (globalLevel) {
    case ACCOUNT_LEVEL:
    case "ACCOUNT":
      preferenceName = RNP_ACCOUNT_LEVEL_LABEL_PREFERENCE;
      break;
    case "PROGRAMME":
    case PROGRAMME_LEVEL:
      preferenceName = RNP_PROGRAMME_LEVEL_LABEL_PREFERENCE;
      break;
    case "LEARNING_COURSE":
    case LEARNING_COURSE_LEVEL:
      preferenceName = RNP_LEARNING_COURSE_LEVEL_LABEL_PREFERENCE;
      break;
    case "COURSE":
    case CLASS_LEVEL:
      preferenceName = RNP_CLASS_LEVEL_LABEL_PREFERENCE;
      break;
    default:
      return null;
  }

  let labelLocale = getSettingValue({
    organizationId,
    name: preferenceName,
  });

  if (
    labelLocale == "programme_level_default_label" &&
    getIsAccountConfigurationEnabled()
  ) {
    labelLocale = "programme_level_default_label_with_learning_course";
  }

  return labelLocale;
};

const getCurrentLevelFromGlobalLevel = globalLevel => {
  // Todo : Merge this function and getApplicableGlobalLevelsFromCurrentLevel post clean up of `ROLES_AND_PERMISSIONS_LEVELS` constant
  const {
    ACCOUNT_LEVEL,
    PROGRAMME_LEVEL,
    LEARNING_COURSE_LEVEL,
    CLASS_LEVEL,
  } = ROLES_AND_PERMISSIONS_LEVELS;
  switch (globalLevel) {
    case "ACCOUNT":
      return ACCOUNT_LEVEL;
    case "PROGRAMME":
      return PROGRAMME_LEVEL;
    case "LEARNING_COURSE":
      return LEARNING_COURSE_LEVEL;
    case "COURSE":
      return CLASS_LEVEL;
    default:
      return null;
  }
};

const getApplicableGlobalLevelsFromCurrentLevel = level => {
  const {
    ACCOUNT_LEVEL,
    PROGRAMME_LEVEL,
    LEARNING_COURSE_LEVEL,
    CLASS_LEVEL,
  } = ROLES_AND_PERMISSIONS_LEVELS;

  const state = store.getState();

  const isLearningCoursesEnabled = _.get(
    state,
    "platform.isLearningCourseEnabled",
    false
  );

  switch (level) {
    case ACCOUNT_LEVEL:
      return isLearningCoursesEnabled
        ? ["ACCOUNT", "PROGRAMME", "LEARNING_COURSE", "COURSE"]
        : ["ACCOUNT", "PROGRAMME", "COURSE"];
    case PROGRAMME_LEVEL:
      return isLearningCoursesEnabled
        ? ["PROGRAMME", "LEARNING_COURSE", "COURSE"]
        : ["PROGRAMME", "COURSE"];
    case LEARNING_COURSE_LEVEL:
      return ["LEARNING_COURSE", "COURSE"];
    case CLASS_LEVEL:
      return ["COURSE"];
    default:
      return [];
  }
};

const getLocaleContextSuffixForRnP = () => {
  if (getIsAccountConfigurationEnabled()) {
    return "with_learning_course";
  }

  if (getIsAdminNavigationFor10Accounts()) {
    return "with_v1_new_admin_nav";
  }

  return "";
};

const isRnPInternalToolRoute = () => {
  const isRnPInternalToolRoute = _.includes(
    getCurrentPathname(),
    "/rolesAndPermissionsTool"
  );
  return isRnPInternalToolRoute;
};

const getBehaviourModuleNomenclature = () => {
  const organizationId = getOrganizationId();
  const behaviourNomenclatureConstants = getBehaviourNomenclatureFromCache({
    organizationId,
  });

  const { MODULE, INCIDENT, ACTION, LEVEL } = behaviourNomenclatureConstants;

  // exclude "management" from moduleName
  const moduleName = _.includes(
    ["Behaviour management", "Conduct management"],
    MODULE
  )
    ? _.get(_.split(MODULE, " "), "0")
    : MODULE;

  return {
    MODULE: moduleName,
    INCIDENT,
    ACTION,
    LEVEL,
  };
};

const isBehaviourModuleEnabled = () => {
  const isBehaviourModuleEnabled =
    ACLStore.can(ENABLE_BEHAVIOUR_MODULE_PERM) || isRnPInternalToolRoute();
  return isBehaviourModuleEnabled;
};

const isBehaviourModuleEnabledWithRnP = () => {
  const isBehaviourModuleEnabledWithRnP =
    ACLStore.can(ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR) ||
    isRnPInternalToolRoute();
  return isBehaviourModuleEnabledWithRnP;
};

const getNomenclatureForBehaviourModuleSpecificLocales = () => {
  if (!isBehaviourModuleEnabledWithRnP()) {
    return {
      MODULE: "",
      INCIDENT: "",
      ACTION: "",
      LEVEL: "",
    };
  }
  return getBehaviourModuleNomenclature();
};

// For module locales that are dependent on the behaviour module nomenclature - eg: student profile, insights, etc.
const getNomenclatureForBehaviourModuleDependentLocales = () => {
  if (!isBehaviourModuleEnabled()) {
    return {
      MODULE: "",
      INCIDENT: "",
      ACTION: "",
      LEVEL: "",
    };
  }
  return getBehaviourModuleNomenclature();
};

const getWithoutBehaviourModuleSuffix = () => {
  const isBehaviourModuleEnabled = isBehaviourModuleEnabledWithRnP();

  return isBehaviourModuleEnabled ? "" : "_without_behaviour_module";
};

const getWithoutBehaviourModuleSuffixForBehaviourDependentPermissions = () => {
  const isBehaviourModuleEnabledFlag = isBehaviourModuleEnabled();

  return isBehaviourModuleEnabledFlag ? "" : "_without_behaviour_module";
};

const isSnPEnabledInAnyCurriculum = () => {
  const state = store.getState();
  const curriculumProgramList = getOrganizationCurriculumPrograms(state);

  const allCurriculumsAdminConfigObject = getAllCurriculumsAdminConfigurationsObject(
    {
      curriculumProgramList,
    }
  );

  const snpConfigs = _.flatMap(allCurriculumsAdminConfigObject, response => {
    return _.filter(response, (_, key) => {
      return key == SNPv2_PERM;
    });
  });

  const isSnPEnabled = _.some(snpConfigs, config => config.value == "true");

  return isSnPEnabled;
};

const getSnPModuleLabel = t => {
  const state = store.getState();
  const organizationId = getOrganizationId(state);
  const snpModuleLabelSetting = getSettingValue({
    name: "AppSettingSnpModuleLabel",
    organizationId,
  });

  const snpModuleLabel = t(`snp:${snpModuleLabelSetting}`, {
    defaultValue: snpModuleLabelSetting,
  });

  return snpModuleLabel;
};

const getEntityLabelAndDescription = ({ localeKey, t }) => {
  const state = store.getState();

  const localeContextSuffixForRnP = getLocaleContextSuffixForRnP();

  switch (localeKey) {
    // Feature flag based locales
    case "permission_can_manage_sign_in_codes_block_access_and_log_students_out_description":
    case "permission_reset_student_sign_in_and_family_invite_codes_block_student_logout_student_for_account_description":
    case "permission_can_reset_student_sign_in_and_family_invite_codes_block_student_logout_student_description": {
      const isStudentBlockingEnabled = isStudentBlockingFeatureEnabled();
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        blockStudentAccessLabel: isStudentBlockingEnabled
          ? t("acl:temporarily_block_student_access")
          : "",
        context: localeContextSuffixForRnP,
      });
    }
    case "permission_can_manage_sign_in_codes_block_access_and_log_students_out":
    case "permission_reset_student_sign_in_and_family_invite_codes_block_student_logout_student_for_account":
    case "permission_can_reset_student_sign_in_and_family_invite_codes_block_student_logout_student": {
      const isStudentBlockingEnabled = isStudentBlockingFeatureEnabled();
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        accessLabel: isStudentBlockingEnabled ? t("acl:access") : "",
        context: localeContextSuffixForRnP,
      });
    }
    case "module_setting_module_settings_view_and_manage_settings_description": {
      const localeKeyBasedOnFeatureFlag = ACLStore.can(
        ENABLE_MODULE_SETTINGS_IN_PROGRAMME_WIDE_CONFIGURATIONS
      )
        ? "module_setting_module_settings_view_and_manage_settings_description_with_module_on_off"
        : localeKey;

      return t(`acl:${localeKeyBasedOnFeatureFlag}`, {
        defaultValue: localeKeyBasedOnFeatureFlag,
        context: localeContextSuffixForRnP,
      });
    }

    // Behaviour module based locales

    case "role_school_head_description":
    case "permission_behaviour_setup_configure_behaviour_settings_and_permission":
    case "module_behaviour_setup":
    case "module_behaviour_dashboard":
    case "permission_can_manage_behaviour_roles_and_permissions":
    case "permission_can_manage_behaviour_roles_and_permissions_description":
    case "module_setting_behaviour_setup_view_behaviour_configurations_module_description": {
      const { MODULE } = getNomenclatureForBehaviourModuleSpecificLocales();
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        module: MODULE,
        context: localeContextSuffixForRnP,
      });
    }
    case "role_teacher_description": {
      const { INCIDENT } = getNomenclatureForBehaviourModuleSpecificLocales();

      const withoutBehaviourModuleSuffix =
        INCIDENT != "" ? "" : "_without_behaviour_module";

      const isAuthAndEvalEnabled = isSnPEnabledInAnyCurriculum();

      const withoutAuthAndEvalSuffix = isAuthAndEvalEnabled
        ? ""
        : "_without_snp_module";

      const localeSuffix = `${withoutBehaviourModuleSuffix}${withoutAuthAndEvalSuffix}`;

      return t(`acl:${localeKey}${localeSuffix}`, {
        defaultValue: localeKey,
        incident: INCIDENT,
        context: localeContextSuffixForRnP,
        snpModuleLabel: getSnPModuleLabel(t),
      });
    }
    case "permission_behaviour_dashboard_override_default_outcomes_description":
    case "permission_behaviour_dashboard_view_all_incidents":
    case "permission_behaviour_dashboard_edit_confidentiality_notes_description":
    case "permission_behaviour_dashboard_view_confidentiality_notes_description":
    case "permission_behaviour_dashboard_create_incident":
    case "permission_behaviour_dashboard_create_incident_description":
    case "permission_behaviour_dashboard_view_all_incidents_description":
    case "permission_behaviour_dashboard_edit_incidents":
    case "permission_behaviour_dashboard_edit_incidents_description":
    case "behaviour_dashboard_can_view_all_incidents_and_confidentiality_notes":
    case "behaviour_dashboard_can_view_all_incidents_and_confidentiality_notes_description":
    case "behaviour_dashboard_can_edit_all_incidents_and_view_confidentiality_notes":
    case "behaviour_dashboard_can_edit_all_incidents_and_view_confidentiality_notes_description":
    case "behaviour_dashboard_can_edit_all_incidents_and_confidentiality_notes":
    case "behaviour_dashboard_can_edit_all_incidents_and_confidentiality_notes_description": {
      const { INCIDENT } = getNomenclatureForBehaviourModuleSpecificLocales();
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        incident: INCIDENT,
        context: localeContextSuffixForRnP,
      });
    }
    case "module_setting_behaviour_dashboard_view_behaviour_dashboard_description": {
      const {
        MODULE,
        INCIDENT,
      } = getNomenclatureForBehaviourModuleSpecificLocales();
      const withoutBehaviourModuleSuffix = getWithoutBehaviourModuleSuffix();
      return t(`acl:${localeKey}${withoutBehaviourModuleSuffix}`, {
        defaultValue: localeKey,
        incident: INCIDENT,
        module: MODULE,
        context: localeContextSuffixForRnP,
      });
    }
    case "permission_behaviour_setup_configure_behaviour_settings_and_permission_description":
    case "module_behaviour_dashboard_v2":
    case "role_behaviour_teacher_description":
    case "role_account_behaviour_advisor":
    case "role_account_behaviour_advisor_description":
    case "role_programme_behaviour_advisor":
    case "role_programme_behaviour_advisor_description":
    case "permission_can_edit_all_incidents_and_view_confidential_notes":
    case "permission_can_edit_all_incidents_and_view_confidential_notes_description":
    case "permission_can_view_behaviour_dashboard":
    case "permission_can_view_behaviour_dashboard_description":
    case "permission_students_can_be_referred_to_all_incidents":
    case "permission_students_can_be_referred_to_all_incidents_description":
    case "permission_students_can_edit_all_incidents_and_view_confidential_notes_for_their_students":
    case "permission_students_can_edit_all_incidents_and_view_confidential_notes_for_their_students_description":
    case "permission_students_can_view_student_profile_and_behaviour_for_all_students":
    case "permission_students_can_view_student_profile_and_behaviour_for_all_students_description":
    case "permission_students_can_view_behaviour_dashboard_student":
    case "permission_students_can_view_behaviour_dashboard_student_description":
    case "permission_students_can_create_incidents_for_all":
    case "permission_students_can_create_incidents_for_all_description":
    case "module_setting_student_profile_and_behaviour":
    case "module_setting_student_profile_and_behaviour_description":
    case "permission_students_can_create_incidents_for_their_students":
    case "permission_students_can_create_incidents_for_their_students_description":
    case "permission_students_can_view_all_incidents":
    case "permission_students_can_view_all_incidents_students_description":
    case "permission_students_can_view_all_incidents_and_confidential_notes":
    case "permission_students_can_view_all_incidents_and_confidential_notes_description":
    case "permission_students_can_edit_delete_all_incidents_and_confidential_notes_for_their_students":
    case "permission_students_can_edit_delete_all_incidents_and_confidential_notes_for_their_students_description":
    case "permission_students_can_edit_all_incidents_and_view_confidential_notes":
    case "permission_students_can_edit_all_incidents_and_view_confidential_notes_description":
    case "permission_students_can_be_referred_to_incidents_for_their_students":
    case "permission_students_can_be_referred_to_incidents_for_their_students_description":
    case "permission_students_can_view_incidents_for_their_students":
    case "permission_students_can_view_incidents_for_their_students_description":
    case "permission_can_edit_delete_all_incidents_and_confidential_notes":
    case "permission_can_edit_delete_all_incidents_and_confidential_notes_description":
    case "permission_can_be_referred_to_incidents":
    case "permission_can_be_referred_to_incidents_description":
    case "permission_students_can_view_incidents_and_confidential_notes_for_their_students":
    case "permission_students_can_view_incidents_and_confidential_notes_for_their_students_description":
    case "permission_students_can_edit_all_incidents":
    case "permission_students_can_edit_all_incidents_description":
    case "permission_students_can_edit_incidents_for_their_students":
    case "permission_students_can_edit_incidents_for_their_students_description":
    case "permission_students_can_edit_delete_all_incidents_and_confidential_notes":
    case "permission_students_can_edit_delete_all_incidents_and_confidential_notes_description":
    case "permission_can_create_incident":
    case "permission_can_create_incident_description":
    case "module_setting_student_profile_and_behaviour_for_their_students":
    case "module_setting_student_profile_and_behaviour_for_their_students_description":
    case "permission_can_edit_all_incidents":
    case "permission_can_edit_all_incidents_description":
    case "permission_can_view_all_incidents":
    case "permission_can_view_all_incidents_description":
    case "permission_can_view_all_incidents_and_confidential_notes":
    case "permission_can_view_all_incidents_and_confidential_notes_description": {
      const {
        MODULE,
        INCIDENT,
        ACTION,
        LEVEL,
      } = getNomenclatureForBehaviourModuleSpecificLocales();
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        module: MODULE,
        incident: INCIDENT,
        action: ACTION,
        level: LEVEL,
        context: localeContextSuffixForRnP,
      });
    }
    case "role_non_admin_user_description": {
      const accountLevelLabelLocale = getRnPLevelLabelLocale("ACCOUNT");

      return t(`acl:${localeKey}`, {
        accountLevel: t(`acl:${accountLevelLabelLocale}`, {
          defaultValue: accountLevelLabelLocale,
        }),
        context: localeContextSuffixForRnP,
      });
    }
    case "role_account_supervisor_description": {
      const accountLevelLabelLocale = getRnPLevelLabelLocale("ACCOUNT");
      const { MODULE } = getNomenclatureForBehaviourModuleSpecificLocales();

      const withoutBehaviourModuleSuffix =
        MODULE != "" ? "" : "_without_behaviour_module";

      return t(`acl:${localeKey}${withoutBehaviourModuleSuffix}`, {
        accountLevel: t(`acl:${accountLevelLabelLocale}`, {
          defaultValue: accountLevelLabelLocale,
        }),
        module: MODULE,
        context: localeContextSuffixForRnP,
      });
    }
    case "module_setting_can_view_programme_wide_configurations_description": {
      const programmeLevelLabelLocale = getRnPLevelLabelLocale("PROGRAMME");
      return t(`acl:${localeKey}`, {
        programLevel: t(`acl:${programmeLevelLabelLocale}`, {
          defaultValue: programmeLevelLabelLocale,
        }),
        context: localeContextSuffixForRnP,
      });
    }
    case "role_principal_description": {
      const programmeLevelLabelLocale = getRnPLevelLabelLocale("PROGRAMME");
      const { MODULE } = getNomenclatureForBehaviourModuleSpecificLocales();
      const withoutBehaviourModuleSuffix = getWithoutBehaviourModuleSuffix();

      return t(`acl:${localeKey}${withoutBehaviourModuleSuffix}`, {
        programmeLevel: t(`acl:${programmeLevelLabelLocale}`, {
          defaultValue: programmeLevelLabelLocale,
        }),
        module: MODULE,
        context: localeContextSuffixForRnP,
      });
    }

    // Behaviour module dependent locales/permissions
    case "permission_analytics_view_behaviour_analytics": {
      const { MODULE } = getNomenclatureForBehaviourModuleDependentLocales();
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        module: MODULE,
        context: localeContextSuffixForRnP,
      });
    }

    case "permission_analytics_view_behaviour_analytics_description": {
      const { INCIDENT } = getNomenclatureForBehaviourModuleDependentLocales();
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        incident: INCIDENT,
        context: localeContextSuffixForRnP,
      });
    }

    case "permission_student_profile_view_other_student_details":
    case "permission_student_profile_view_other_student_details_description":
    case "permission_student_profile_view_other_student_details_2":
    case "permission_student_profile_view_other_student_details_description_2": {
      const {
        MODULE,
        INCIDENT,
      } = getNomenclatureForBehaviourModuleDependentLocales();
      const withoutBehaviourModuleSuffix = getWithoutBehaviourModuleSuffixForBehaviourDependentPermissions();
      return t(`acl:${localeKey}${withoutBehaviourModuleSuffix}`, {
        defaultValue: localeKey,
        incident: INCIDENT,
        module: MODULE,
        context: localeContextSuffixForRnP,
      });
    }

    case "permission_student_profile_can_view_behaviour_incidents_for_student":
    case "permission_student_profile_can_view_behaviour_incidents_for_student_description": {
      const {
        MODULE,
        INCIDENT,
      } = getNomenclatureForBehaviourModuleDependentLocales();
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        incident: INCIDENT,
        module: MODULE,
        context: localeContextSuffixForRnP,
      });
    }

    // SnP module specific locales
    case "permission_authorisation_and_evaluation_manage_evaluation_cycles_description":
    case "module_authorisation_and_evaluation":
    case "module_setting_authorisation_and_evaluation_view_and_suggest_evidences_in_authorisation_and_evaluation_module_description":
    case "module_setting_authorisation_and_evaluation_view_and_suggest_evidences_in_authorisation_and_evaluation_module": {
      const snpModuleLabel = getSnPModuleLabel(t);
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        snpModuleLabel: snpModuleLabel,
        context: localeContextSuffixForRnP,
      });
    }

    //Calendar module specific locales

    case "permission_calendar_manage_calendar_events_and_meetings": {
      // Manage school holidays and events
      if (isNewEventCreationFlowEnabled()) {
        return t(`acl:${localeKey}`, {
          defaultValue: localeKey,
          context: localeContextSuffixForRnP,
        });
      } else {
        return t(
          "acl:permission_calendar_manage_calendar_events_and_meetings_old",
          // Manage calendar events and meetings
          {
            defaultValue: localeKey,
            context: localeContextSuffixForRnP,
          }
        );
      }
    }

    // Can create, edit and delete holidays and whole school events for the programmes they have access to
    case "permission_calendar_manage_calendar_events_and_meetings_description_v2":
    case "permission_calendar_manage_calendar_events_and_meetings_description_v2_with_learning_course": {
      // Can create, edit and delete holidays and whole school events for the schools they have access to
      if (isNewEventCreationFlowEnabled()) {
        return t(`acl:${localeKey}`, {
          defaultValue: localeKey,
          context: localeContextSuffixForRnP,
        });
      } else {
        return t(
          "acl:permission_calendar_manage_calendar_events_and_meetings_description_old",
          // Can create, edit, delete events and video meetings in the calendar for the programmes they have access to
          {
            defaultValue: localeKey,
            context: localeContextSuffixForRnP,
          }
        );
      }
    }

    case "permission_can_create_assignments_in_calendar": {
      // Calendar
      if (isNewEventCreationFlowEnabled()) {
        return t(
          "acl:permission_calendar_manage_events_assignments_and_video_meetings_on_calendar",
          // Manage events, assignments and video meetings on calendar
          {
            defaultValue: localeKey,
            context: localeContextSuffixForRnP,
          }
        );
      } else {
        return t(`acl:${localeKey}`, {
          defaultValue: localeKey,
          context: localeContextSuffixForRnP,
        });
      }
    }

    case "permission_can_create_assignments_in_calendar_description_v2": {
      // Can create, edit and delete events, assignment and video meeting for the classes they have access to
      if (isNewEventCreationFlowEnabled()) {
        return t(`acl:${localeKey}`, {
          defaultValue: localeKey,
          context: localeContextSuffixForRnP,
        });
      } else {
        return t(
          "acl:permission_can_create_assignments_in_calendar_description_old",
          // Can create video meetings and assignments in the calendar for the classes they have access to
          {
            defaultValue: localeKey,
            context: localeContextSuffixForRnP,
          }
        );
      }
    }

    //learning course specific permissions
    case "module_setting_school_roster_for_account_description":
    case "permission_manage_school_roster_for_account_description": {
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        context: localeContextSuffixForRnP,
      });
    }
    case "module_setting_staff_permissions_view_rnp_module_description":
    case "module_setting_roles_and_permissions_description": {
      const accountLevelLabelLocale = getRnPLevelLabelLocale("ACCOUNT");
      const programLevelLabelLocale = getRnPLevelLabelLocale("PROGRAMME");
      const classLevelLabelLocale = getRnPLevelLabelLocale("COURSE");
      const learningCourseLevelLabelLocale = getRnPLevelLabelLocale(
        "LEARNING_COURSE"
      );

      return t(`acl:${localeKey}`, {
        accountLevel: t(`acl:${accountLevelLabelLocale}`, {
          defaultValue: accountLevelLabelLocale,
        }),
        programLevel: t(`acl:${programLevelLabelLocale}`, {
          defaultValue: programLevelLabelLocale,
        }),
        classLevel: t(`acl:${classLevelLabelLocale}`, {
          defaultValue: classLevelLabelLocale,
        }),
        learningCourseLevel: t(`acl:${learningCourseLevelLabelLocale}`, {
          defaultValue: learningCourseLevelLabelLocale,
        }),
        context: localeContextSuffixForRnP,
      });
    }
    case "permission_staff_permissions_manage_programme_and_class_level_roles_description":
    case "permission_staff_permissions_manage_programme_and_class_level_roles":
    case "permission_roles_and_permissions_manage_non_account_level_roles_description":
    case "permission_roles_and_permissions_manage_non_account_level_roles": {
      const programLevelLabelLocale = getRnPLevelLabelLocale("PROGRAMME");
      const classLevelLabelLocale = getRnPLevelLabelLocale("COURSE");
      const learningCourseLevelLabelLocale = getRnPLevelLabelLocale(
        "LEARNING_COURSE"
      );

      return t(`acl:${localeKey}`, {
        programLevel: t(`acl:${programLevelLabelLocale}`, {
          defaultValue: programLevelLabelLocale,
        }),
        classLevel: t(`acl:${classLevelLabelLocale}`, {
          defaultValue: classLevelLabelLocale,
        }),
        learningCourseLevel: t(`acl:${learningCourseLevelLabelLocale}`, {
          defaultValue: learningCourseLevelLabelLocale,
        }),
        context: localeContextSuffixForRnP,
      });
    }
    case "permission_staff_permissions_manage_account_level_roles_description":
    case "permission_staff_permissions_manage_account_level_roles":
    case "permission_roles_and_permissions_manage_account_level_roles_description":
    case "permission_roles_and_permissions_manage_account_level_roles": {
      const accountLevelLabelLocale = getRnPLevelLabelLocale("ACCOUNT");

      return t(`acl:${localeKey}`, {
        accountLevel: t(`acl:${accountLevelLabelLocale}`, {
          defaultValue: accountLevelLabelLocale,
        }),
        context: localeContextSuffixForRnP,
      });
    }

    default:
      return t(`acl:${localeKey}`, {
        defaultValue: localeKey,
        context: localeContextSuffixForRnP,
      });
  }
};

const getParentNodeOfChild = ({
  childId,
  nodesData,
  isAssociatedChildNode,
  shouldConsiderModuleSettingsNodeAsParent = true,
  shouldFetchGrandParentNode = false,
}) => {
  if (isAssociatedChildNode) {
    const childNode = _.find(nodesData, item => {
      return item.id === childId;
    });

    const parentNode = _.find(nodesData, item => {
      if (shouldConsiderModuleSettingsNodeAsParent) {
        const currentLevel = getCurrentLevelFromGlobalLevel(item.globalLevel);
        const applicableLevels = getApplicableGlobalLevelsFromCurrentLevel(
          currentLevel
        );

        const isModuleSettingParentNodePresent = _.includes(
          applicableLevels,
          childNode?.associatedParentGlobalLevel
        );

        if (isModuleSettingParentNodePresent) {
          return (
            _.includes(item.children, childId) &&
            childNode?.associatedParentGlobalLevel == item.globalLevel
          );
        }
      }

      return (
        _.includes(item.children, childId) &&
        item.globalLevel === childNode?.globalLevel
      );
    });
    return parentNode;
  }

  const parentNode = _.find(
    nodesData,
    ({ children = [], childrenModules = [] }) => {
      const permissionsAndModuleSettingsChildren = [
        ...children,
        ...childrenModules,
      ];
      return _.includes(permissionsAndModuleSettingsChildren, childId);
    }
  );

  if (shouldFetchGrandParentNode && parentNode?.isChildrenModule) {
    return getParentNodeOfChild({
      childId: parentNode?.id,
      nodesData,
      isAssociatedChildNode: false,
    });
  }

  return parentNode;
};

const getChildrenNodeDetailsOfParentNode = ({ childIds, nodesData, t }) => {
  const childrenDetails = _.reduce(
    nodesData,
    (
      childrenDetails,
      {
        label,
        value,
        id,
        isChildrenModule = false,
        children = [],
        childrenModules = [],
        isPseudoModuleSettingsNode = false,
      }
    ) => {
      if (_.includes(childIds, id)) {
        const childNode = {
          id,
          label,
          value,
          isChildrenModule,
          isPseudoModuleSettingsNode,
          children,
          childrenModules,
        };

        // If it's a childrenModule, recursively fetch its children details
        if (isChildrenModule && !_.isEmpty(children)) {
          const grandChildrenDetails = getChildrenNodeDetailsOfParentNode({
            childIds: children,
            nodesData,
            t,
          });

          for (const grandChild of grandChildrenDetails) {
            childrenDetails.push({
              ...grandChild,
              isGrandChild: true,
            });
          }
        }

        childrenDetails.push(childNode);
      }
      return childrenDetails;
    },
    []
  );
  return childrenDetails;
};

const getPermissionsWithHierarchy = ({ permissions, nodes }) => {
  const permissionsWithHierarchy = {};
  const generatedParentNodesForChildPermissionsNotHavingParent = {};

  /**
   * For each parent node, we need to include the parent node and all the children nodes
   * For each child node, we need to check if its parent node and that child node is not already included
   * If the child node doesn't have a parent node, then we need to create a parent node for that child node to display in the UI
   */

  const keysForChildPermissionsHavingParent = _.flatMap(
    permissions,
    ({
      children = [],
      isChildrenModule,
      childrenModules = [],
      isGrandChild,
      id,
    }) => {
      /**
       * We only show level 0 parent i.e. module node in the UI
       * So if the node is a children module setting node ( level 1 node ), then we include its parent node ( level 0 node ).
       * For child node ( level 2 node ) of a children module setting node ( level 1 node ),
       * we include the grand parent node (level 0 parent node )
       */

      const keysForChildPermissionsAndModuleSettingsHavingParent = !isChildrenModule
        ? [...children, ...childrenModules]
        : [];

      if (isGrandChild) {
        const parent = getParentNodeOfChild({
          childId: id,
          nodesData: nodes,
          isAssociatedChildNode: false,
        });

        const grandParent = getParentNodeOfChild({
          childId: parent?.id,
          nodesData: nodes,
          isAssociatedChildNode: false,
        });

        if (permissions[grandParent.id]) {
          keysForChildPermissionsAndModuleSettingsHavingParent.push(id);
        }
      }
      return keysForChildPermissionsAndModuleSettingsHavingParent;
    }
  );

  const parentKeys = _.keys(permissions).filter(key => {
    const {
      isParentNode,
      isPseudoModuleSettingsNode,
      isChildrenModule,
    } = permissions[key];

    return (isParentNode && !isChildrenModule) || isPseudoModuleSettingsNode;
  });

  /**
   * Get all the children keys that doesn't have a parent node updated in the `permissions` object
   * This is required to create a parent node for the child node that doesn't have a parent node already included in the `permissions` object
   */

  const keysForChildPermissionsNotHavingParent = _.keys(permissions).filter(
    key => {
      const { isParentNode, isChildrenModule } = permissions[key] || {};

      return (
        (!isParentNode || isChildrenModule) &&
        !_.includes(keysForChildPermissionsHavingParent, key)
      );
    }
  );

  for (const key of parentKeys) {
    const permission = permissions[key];
    const { children, childrenModules } = permission;
    permissionsWithHierarchy[key] = permission;

    //Include children module settings and their children as well ( grandChildren of current module setting node )
    if (!_.isEmpty(childrenModules)) {
      for (const childId of childrenModules) {
        if (permissions[childId]) {
          permissionsWithHierarchy[childId] = permissions[childId];
        }
        const childModuleSettingsNode = nodes.find(node => node.id === childId);
        const grandChildren = childModuleSettingsNode?.children;
        if (!_.isEmpty(grandChildren)) {
          for (const grandChildId of grandChildren) {
            if (!permissions[grandChildId]) continue;
            permissionsWithHierarchy[grandChildId] = permissions[grandChildId];
          }
        }
      }
    }

    // Include all the children nodes that are not children module settings and are normal permission nodes
    if (!_.isEmpty(children)) {
      for (const childId of children) {
        if (!permissions[childId]) continue;
        permissionsWithHierarchy[childId] = permissions[childId];
      }
    }
  }

  for (const key of keysForChildPermissionsNotHavingParent) {
    const parentNode = getParentNodeOfChild({
      childId: key,
      nodesData: nodes,
      isAssociatedChildNode: permissions[key]?.isAssociatedChildNode,
      shouldFetchGrandParentNode: true,
    });

    /**
     * In order to maintain same parent node across all the associated child nodes of different levels
     * we need to split the parent node id i.e. extract `associatedParentId` from the `associatedParentId_globalLevel` key
     */

    const parentId = _.split(parentNode?.id, "_")[0];

    if (!generatedParentNodesForChildPermissionsNotHavingParent[parentId]) {
      generatedParentNodesForChildPermissionsNotHavingParent[parentId] = {
        ...parentNode,
        isGeneratedParentNodeForUI: true,
        isParentNode: true,
        generatedChildren: [key],
      };
    } else {
      generatedParentNodesForChildPermissionsNotHavingParent[
        parentId
      ]?.generatedChildren.push(key);
    }
  }

  for (const key in generatedParentNodesForChildPermissionsNotHavingParent) {
    permissionsWithHierarchy[key] =
      generatedParentNodesForChildPermissionsNotHavingParent[key];

    const generatedParentNodeChildren =
      generatedParentNodesForChildPermissionsNotHavingParent[key]
        ?.generatedChildren;

    const generatedParentNodeChildrenModules = _.filter(
      generatedParentNodeChildren,
      childId => permissions[childId]?.isChildrenModule
    );

    const generatedParentNodeChildrenPermissions = _.filter(
      generatedParentNodeChildren,
      childId => !permissions[childId]?.isChildrenModule
    );

    // Include all the children modules node and their children ( level 2 children ) as well
    for (const childId of generatedParentNodeChildrenModules) {
      permissionsWithHierarchy[childId] = permissions[childId];
      const grandChildren = permissions[childId]?.children;
      for (const grandChildId of grandChildren) {
        if (!permissions[grandChildId]) continue;
        permissionsWithHierarchy[grandChildId] = permissions[grandChildId];
      }
    }

    for (const childId of generatedParentNodeChildrenPermissions) {
      if (!permissionsWithHierarchy[childId]) {
        permissionsWithHierarchy[childId] = permissions[childId];
      }
    }
  }

  return permissionsWithHierarchy;
};

const getTableRowStyle = ({ isParentNode, isPermissionGroupNode }) => {
  if (isParentNode) {
    return { backgroundColor: "var(--surface-secondary-enabled)" };
  }
  if (isPermissionGroupNode) {
    return {
      backgroundColor: "var(--surface-tertiary-enabled)",
    };
  }
  return {};
};

const getRolesAssignedUsersFeedQueryVariables = params => {
  const {
    first,
    roleId,
    level,
    curriculumPrograms,
    allCurriculumProgramIds,
    courses,
    allCourseIds,
    learningCourses,
    allLearningCourseIds,
    searchText,
    orderBy,
    orderByDirection,
    secondOrderBy,
    secondOrderByDirection,
    archivalState,
    academicYearId,
    roleModule,
  } = params;
  const {
    ACCOUNT_LEVEL,
    LEARNING_COURSE_LEVEL,
    PROGRAMME_LEVEL,
    CLASS_LEVEL,
  } = ROLES_AND_PERMISSIONS_LEVELS;

  switch (level) {
    case ACCOUNT_LEVEL:
      return {
        roleId,
        first,
        searchText,
        orderBy,
        orderByDirection,
        secondOrderBy,
        secondOrderByDirection,
        archivalState,
        academicYearId,
        assignedUsersContextFilters: {
          academicYearId,
        },
      };
    case PROGRAMME_LEVEL: {
      /**
       * To ensure we only receive the curriculum program context in the response from `rnpRoleContextDetail`,
       * we need to include all the curriculum programmes as context.
       */
      const roleContextFilter = _.map(
        allCurriculumProgramIds,
        curriculumProgram => {
          return {
            contextType: "CURRICULUM_PROGRAM",
            contextId: curriculumProgram,
          };
        }
      );

      /**
       * To filter users based on the curriculum programmes,
       * we need to include the selected curriculum programmes filter in the `assignedUsers` resolver.
       */
      const usersContextFilter = _.map(
        curriculumPrograms,
        curriculumProgram => {
          return {
            contextType: "CURRICULUM_PROGRAM",
            contextId: curriculumProgram,
          };
        }
      );

      return {
        roleId,
        first,
        searchText,
        orderBy,
        orderByDirection,
        secondOrderBy,
        secondOrderByDirection,
        archivalState,
        academicYearId,
        assignedUsersContextFilters: {
          contextFilters: usersContextFilter,
          rnpRoleId: roleId,
          academicYearId,
        },
        rnpRoleContextFilters: {
          contextFilters: roleContextFilter,
          rnpRoleId: roleId,
          roleModule,
        },
      };
    }

    case CLASS_LEVEL: {
      /**
       * To ensure we only receive the course context in the response from `rnpRoleContextDetail`,
       * we need to include all the courses as context.
       */
      const roleContextFilter = _.map(allCourseIds, course => {
        return {
          contextType: "COURSE",
          contextId: course,
        };
      });

      /**
       * To filter users based on the courses,
       * we need to include the selected courses filter in the `assignedUsers` resolver.
       */

      const usersContextFilter = _.isEmpty(courses)
        ? roleContextFilter
        : _.map(courses, course => {
            return {
              contextType: "COURSE",
              contextId: course,
            };
          });

      return {
        roleId,
        first,
        searchText,
        orderBy,
        orderByDirection,
        secondOrderBy,
        secondOrderByDirection,
        archivalState,
        academicYearId,
        assignedUsersContextFilters: {
          contextFilters: usersContextFilter,
          rnpRoleId: roleId,
          academicYearId,
        },
        rnpRoleContextFilters: {
          contextFilters: roleContextFilter,
          rnpRoleId: roleId,
        },
      };
    }
    case LEARNING_COURSE_LEVEL: {
      /**
       * To ensure we only receive the learningCourse context in the response from `rnpRoleContextDetail`,
       * we need to include all the learningCourses as context.
       */
      const roleContextFilter = _.map(allLearningCourseIds, learningCourse => {
        return {
          contextType: "LEARNING_COURSE",
          contextId: learningCourse,
        };
      });

      /**
       * To filter users based on the learningCourses,
       * we need to include the selected learningCourses filter in the `assignedUsers` resolver.
       */
      const usersContextFilter = _.isEmpty(learningCourses)
        ? roleContextFilter
        : _.map(learningCourses, learningCourse => {
            return {
              contextType: "LEARNING_COURSE",
              contextId: learningCourse,
            };
          });

      return {
        roleId,
        first,
        searchText,
        orderBy,
        orderByDirection,
        secondOrderBy,
        secondOrderByDirection,
        archivalState,
        academicYearId,
        assignedUsersContextFilters: {
          contextFilters: usersContextFilter,
          rnpRoleId: roleId,
          academicYearId,
        },
        rnpRoleContextFilters: {
          contextFilters: roleContextFilter,
          rnpRoleId: roleId,
        },
      };
    }
  }
};

const getRefetchQueries = ({ isManageRolesScreen = false, variables }) => {
  return [
    isManageRolesScreen
      ? {
          query: getRnPLevelsFeedQuery,
          variables,
        }
      : {
          query: getRnPPermissionsFeedQuery,
          variables,
        },
  ];
};

const getIconComponent = iconName => {
  return ICON_COMPONENTS[iconName] || null;
};

const getLearningCourseSuffix = () => {
  const isLearningCoursesEnabled = getIsLearningCoursesEnabledForOrganization();
  return isLearningCoursesEnabled ? "with_learning_course" : "";
};

const getRolesHierarchyForHelpText = ({ t }) => {
  const MULTIPLE_ROLES_KEY = "MULTIPLE_ROLES";

  const {
    ACCOUNT_LEVEL,
    PROGRAMME_LEVEL,
    LEARNING_COURSE_LEVEL,
    CLASS_LEVEL,
  } = ROLES_AND_PERMISSIONS_LEVELS;

  const {
    ACCOUNT_ROLES,
    PROGRAMME_ROLES,
    LEARNING_COURSE_ROLES,
    CLASS_ROLES,
  } = ROLES_AND_PERMISSIONS_TABS;

  const isLearningCoursesEnabled = getIsLearningCoursesEnabledForOrganization();
  const toddle20LocaleSuffix = getIsAccountConfigurationEnabled()
    ? "_with_toddle2"
    : "";
  const learningCourseSuffix = getLearningCourseSuffix();

  const accountLevelLabelLocale = getRnPLevelLabelLocale(ACCOUNT_LEVEL);
  const programmeLevelLabelLocale = getRnPLevelLabelLocale(PROGRAMME_LEVEL);
  const learningCourseLevelLabelLocale = getRnPLevelLabelLocale(
    LEARNING_COURSE_LEVEL
  );
  const classLevelLabelLocale = getRnPLevelLabelLocale(CLASS_LEVEL);

  const accountLevelLabel = t(`acl:${accountLevelLabelLocale}`, {
    defaultValue: accountLevelLabelLocale,
  });

  const programmeLevelLabel = t(`acl:${programmeLevelLabelLocale}`, {
    defaultValue: programmeLevelLabelLocale,
  });

  const learningCourseLevelLabel = t(`acl:${learningCourseLevelLabelLocale}`, {
    defaultValue: learningCourseLevelLabelLocale,
  });

  const classLevelLabel = t(`acl:${classLevelLabelLocale}`, {
    defaultValue: classLevelLabelLocale,
  });

  const ROLES_HIERARCHY_INFO = [
    {
      key: ACCOUNT_ROLES,
      title: t("acl:level_label_with_roles", {
        label: accountLevelLabel,
      }),
      description: t(
        `acl:account_roles_heirarchy_info${toddle20LocaleSuffix}`,
        {
          name: accountLevelLabel,
        }
      ),
      setupAssistantDescription: getEntityLabelAndDescription({
        localeKey: "setup_assistant_account_level_description",
        t,
      }),
      logo: <SchoolOutlined />,
      avatarColor: "yellow",
      examples: [
        {
          key: `${ACCOUNT_ROLES}_EXAMPLE_1`,
          label: t("acl:account_level_help_section_example_1"),
        },
        {
          key: `${ACCOUNT_ROLES}_EXAMPLE_2`,
          label: t("acl:account_level_help_section_example_2"),
        },
        {
          key: `${ACCOUNT_ROLES}_EXAMPLE_3`,
          label: t("acl:account_level_help_section_example_3"),
        },
        {
          key: `${ACCOUNT_ROLES}_EXAMPLE_4`,
          label: t(
            `acl:account_level_help_section_example_4${toddle20LocaleSuffix}`,
            {
              accountLevelLabel,
              context: learningCourseSuffix,
            }
          ),
        },
      ],
    },
    {
      key: PROGRAMME_ROLES,
      title: t("acl:level_label_with_roles", {
        label: programmeLevelLabel,
      }),
      description: t(
        `acl:programme_roles_heirarchy_info${toddle20LocaleSuffix}`,
        {
          name: programmeLevelLabel,
        }
      ),
      setupAssistantDescription: getEntityLabelAndDescription({
        localeKey: "setup_assistant_program_level_description",
        t,
      }),
      logo: <GraduationCapOutlined />,
      avatarColor: "pink",
      examples: [
        {
          key: `${PROGRAMME_ROLES}_EXAMPLE_1`,
          label: t(
            `acl:programme_level_help_section_example_1${toddle20LocaleSuffix}`
          ),
        },
        {
          key: `${PROGRAMME_ROLES}_EXAMPLE_2`,
          label: t(
            `acl:programme_level_help_section_example_2${toddle20LocaleSuffix}`
          ),
        },
        {
          key: `${PROGRAMME_ROLES}_EXAMPLE_3`,
          label: t(
            `acl:programme_level_help_section_example_3${toddle20LocaleSuffix}`
          ),
        },
      ],
    },
    ...(isLearningCoursesEnabled
      ? [
          {
            key: LEARNING_COURSE_ROLES,
            title: t("acl:level_label_with_roles", {
              label: learningCourseLevelLabel,
            }),
            description: t("acl:learning_course_roles_heirarchy_info", {
              name: learningCourseLevelLabel,
            }),
            setupAssistantDescription: getEntityLabelAndDescription({
              localeKey: "setup_assistant_learning_course_level_description",
              t,
            }),
            logo: <OpenBookOutlined />,
            avatarColor: "violet",
            examples: [
              {
                key: `${LEARNING_COURSE_ROLES}_EXAMPLE_1`,
                label: t("acl:learning_course_level_help_section_example_1"),
              },
              {
                key: `${LEARNING_COURSE_ROLES}_EXAMPLE_2`,
                label: t("acl:learning_course_level_help_section_example_2"),
              },
            ],
          },
        ]
      : []),
    {
      key: CLASS_ROLES,
      title: t("acl:level_label_with_roles", {
        label: classLevelLabel,
      }),
      description: t("acl:class_roles_heirarchy_info", {
        name: classLevelLabel,
      }),
      setupAssistantDescription: getEntityLabelAndDescription({
        localeKey: "setup_assistant_class_level_description",
        t,
      }),
      logo: <ClassProgressOutlined />,
      avatarColor: "blue",
      examples: [
        {
          key: `${CLASS_ROLES}_EXAMPLE_1`,
          label: t("acl:class_level_help_section_example_1", {
            context: learningCourseSuffix,
          }),
        },
        {
          key: `${CLASS_ROLES}_EXAMPLE_2`,
          label: t("acl:class_level_help_section_example_2"),
        },
        {
          key: `${CLASS_ROLES}_EXAMPLE_3`,
          label: t("acl:class_level_help_section_example_3"),
        },
      ],
    },
    {
      key: MULTIPLE_ROLES_KEY,
      title: t("acl:multiple_level_roles_title_question"),
      description: t("acl:multiple_level_roles_heirarchy_info", {
        accountLevelLabel,
        programmeLevelLabel,
        classLevelLabel,
        learningCourseLevelLabel,
        context: learningCourseSuffix,
      }),
      examples: [
        {
          key: `${MULTIPLE_ROLES_KEY}_EXAMPLE_1`,
          label: t(
            `acl:multiple_level_help_section_example_1${toddle20LocaleSuffix}`,
            {
              name: programmeLevelLabel,
              context: learningCourseSuffix,
            }
          ),
        },
        {
          key: `${MULTIPLE_ROLES_KEY}_EXAMPLE_2`,
          label: t("acl:multiple_level_help_section_example_2", {
            name: classLevelLabel,
            context: learningCourseSuffix,
          }),
        },
        {
          key: `${MULTIPLE_ROLES_KEY}_EXAMPLE_3`,
          label: t("acl:multiple_level_help_section_example_3", {
            context: learningCourseSuffix,
          }),
        },
      ],
    },
  ];

  return ROLES_HIERARCHY_INFO;
};

const getAcademicYearSubtext = ({ selectedAcademicYear, t }) => {
  const ayLabel = getAcademicYearLabel({
    startDate: _.get(selectedAcademicYear, "startDate"),
    endDate: _.get(selectedAcademicYear, "endDate"),
  });

  return _.get(selectedAcademicYear, "isCurrentAcademicYear", false)
    ? t("common:current_academic_year_with_label", { label: ayLabel })
    : ayLabel;
};

const getStaffMemberStatusPrefixText = ({ assignedUsersTotalCount, t }) => {
  if (assignedUsersTotalCount === 0) return "";
  return t("common:active_lowercase");
};

const getStatusPrefixText = ({ status, t }) => {
  if (status === "ACTIVE") return t("common:active_lowercase");
  if (status === "ARCHIVED") return t("common:archived_lowercase");
  return "";
};

export {
  getActionMenuTabs,
  getActionMenuTabsV2,
  getColumnList,
  getRnPFeedTableHeaders,
  generateRnPFeedTableData,
  onActionItemClick,
  getUsersMappedModalColumns,
  getParentNodeOfChild,
  getPermissionsWithHierarchy,
  getChildrenNodeDetailsOfParentNode,
  getTableRowStyle,
  getLevelFromNodeId,
  getRolesAssignedUsersFeedQueryVariables,
  getApplicableGlobalLevelsFromCurrentLevel,
  getRefetchQueries,
  getLevelFilterEnumFromRnPLevel,
  getRnPLevelNameFromGlobalLevel,
  getIconComponent,
  getEntityLabelAndDescription,
  getCurrentLevelFromGlobalLevel,
  canManageRolesAndPermissions,
  isCustomRoleCreationAllowed,
  getIsBigOrgRnPChangesEnabled,
  getLocaleContextSuffixForRnP,
  getLearningCourseSuffix,
  getRolesHierarchyForHelpText,
  getAcademicYearSubtext,
  getStaffMemberStatusPrefixText,
  getStatusPrefixText,
  getDateInSchoolTimezone,
  ROLES_AND_PERMISSIONS_TABS,
  ROLES_AND_PERMISSIONS_LEVELS,
  LEVELS_ROLES_MAP,
  DEFAULT_ROLE_UID_AND_LEVEL_MAP,
};
